import { BaseSyntheticEvent, useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Helmet } from 'react-helmet-async'
import { CorpHeader } from '../../common/corp-header'
import { CorpFooter } from '../../common/corp-footer'
import contactImg from '../../images/img-ttl-contact.svg'
import axios from "axios";
import TagManager from 'react-gtm-module'
import {
  ErrorInfo,
  required,
  phoneRegexp,
  emailRegexp,
  maxLength,
  shimeiRegexp,
  zenKanaOnly,
} from "../../common/Validator";

interface ContactForm {
  fullName: string;
  kanaName: string;
  corp: string;
  email: string;
  phoneNumber: string;
  contactCategory: string;
  message?: string;
}

interface HideState {
  all: boolean
  corp: boolean
}

export const Contacts = () => {
  const [footerRect, setFooterRect] = useState<DOMRect>()
  const [hideState, setHideState] = useState<HideState>({ all: false, corp: true })
  const [btnClz, setBtnClz] = useState(["btnSubmit"])
  const history = useHistory();
  const ftr = useRef<HTMLElement>(null)
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ContactForm>();

  // フォーム送信
  const onSubmit: SubmitHandler<ContactForm> = async (data: ContactForm) => {
    try {

      setBtnClz([...btnClz, "sending"])// くるくるアイコン
      await axios.post("/api/v1/sp/contacts", data);
      setHideState({ ...hideState, all: true })
    } catch (e) {
      // internal or validationエラー
      console.error(e)
      history.push("/errors/400")
    }
  };

  // カテゴリを監視
  const categoryInput = (e: BaseSyntheticEvent) => {
    setHideState({ ...hideState, corp: e.currentTarget.value === "APP_SERVICE" })
  }

  // フッタのサイズを監視
  useEffect(() => {
    setFooterRect(ftr?.current?.getBoundingClientRect())
  }, [ftr])

  useEffect(() => {
    TagManager.initialize({ gtmId: 'GTM-WC3P65B' })
  }, [])

  return (
    <div className='contact'>
      <div className="bodywrap">
        <Helmet title="お問い合わせ | Family Works" />
        <CorpHeader img={contactImg} imgAlt="CONTACT" titleText="お問い合わせ" />

        <main className="wrapper" style={{ paddingBottom: `${footerRect?.height || "400"}px` }}>
          {!hideState.all && <>
            <p className="contactExplain"><b><u>ファミリーネットワークサービスに関するお問い合わせフォームです。<br/>三井住友銀行の口座やキャッシュカード等の商品・サービスに関するお問い合わせについては回答いたしかねますので、あらかじめご了承ください。</u></b></p>
            <p className="contactExplain"><span className="kome">&#8251;</span>は必須項目です。</p>
            <section className="secContact">
              <form onSubmit={handleSubmit(onSubmit)} id="formContact">
                <ul className="formlist">
                  <li className="formlist__half">
                    <dl>
                      <dt><label htmlFor="frm-category">お問い合わせ項目 <span className="kome">&#8251;</span></label></dt>
                      <dd>
                        <select
                          onInput={categoryInput}
                          defaultValue="APP_SERVICE"
                          {...register("contactCategory", { required })}
                          id="frm-category" tabIndex={1} autoFocus>
                          <option value="APP_SERVICE">アプリ・サービスに関するお問い合わせ</option>
                          <option value="PUBLIC_RELATIONS">広報・取材について</option>
                          <option value="ETC">その他</option>
                        </select>
                      </dd>
                      <ErrorInfo error={errors.contactCategory} />
                    </dl>
                  </li>
                  <li className="required">
                    <dl>
                      <dt><label htmlFor="frm-name">お名前 <span className="kome">&#8251;</span></label></dt>
                      <dd>
                        <input
                          {...register("fullName", { required, pattern: shimeiRegexp, ...maxLength(60), })}
                          type="text" id="frm-name" maxLength={60} placeholder="お名前" tabIndex={2} /></dd>
                      <ErrorInfo error={errors.fullName} />
                    </dl>
                  </li>
                  <li>
                    <dl>
                      <dt><label htmlFor="frm-name">フリガナ<span className="kome">&#8251;</span></label></dt>
                      <dd><input
                        {...register("kanaName", { required, pattern: zenKanaOnly, ...maxLength(60), })}
                        type="text" id="frm-name" maxLength={60} placeholder="フリガナ" tabIndex={3} /></dd>
                      <ErrorInfo error={errors.kanaName} />
                    </dl>
                  </li>
                  <li className={`formlist__full hide-category1 ${hideState.corp ? 'hide' : ''}`}>
                    <dl>
                      <dt><label htmlFor="frm-corp">会社名・部署名</label></dt>
                      <dd><input
                        {...register("corp", { ...maxLength(60), })}
                        type="text" name="corp" id="frm-corp" maxLength={60} placeholder="会社名・部署名" tabIndex={4} />
                      </dd>
                      <ErrorInfo error={errors.corp} />
                    </dl>
                  </li>
                  <li className="required">
                    <dl>
                      <dt><label htmlFor="frm-email">メールアドレス <span className="kome">&#8251;</span></label></dt>
                      <dd><input
                        {...register("email", { required, pattern: emailRegexp, ...maxLength(254), })}
                        type="email" id="frm-email" maxLength={254} placeholder="メールアドレス" tabIndex={5} /></dd>
                      <ErrorInfo error={errors.email} />
                    </dl>
                  </li>
                  <li>
                    <dl>
                      <dt><label htmlFor="frm-tel">電話番号</label></dt>
                      <dd><input
                        {...register("phoneNumber", { pattern: phoneRegexp, ...maxLength(11), })}
                        type="text" id="frm-tel" maxLength={11} placeholder="電話番号" tabIndex={6} /></dd>
                      <ErrorInfo error={errors.phoneNumber} />
                    </dl>
                  </li>
                  <li className="formlist__full required">
                    <dl>
                      <dt><label htmlFor="frm-msg">お問い合わせ内容 <span className="kome">&#8251;</span></label></dt>
                      <dd><textarea
                        {...register("message", { required })}
                        id="frm-msg" cols={50} rows={5} placeholder="具体的な内容を記入してください。" tabIndex={7}></textarea></dd>
                      <ErrorInfo error={errors.message} />
                    </dl>
                  </li>
                </ul>
                <div className={btnClz.join(' ')}>
                  <div className="iconSpinner"></div>
                  <input type="submit" name="submit" value="送信" tabIndex={8} />
                </div>
              </form>
              <p className="contactNote">お問い合わせの受付完了メールおよび担当者からの回答は、入力いただいたメールアドレスへ送信いたします。また、お問い合わせ内容によっては回答いたしかねる場合もございますので、あらかじめご了承ください。なお、土日祝日、年末年始（12/30～1/3）にいただいたお問い合わせは、翌営業日以降に順次ご回答いたします。</p>
            </section>
          </>}
          {hideState.all && <section className="secContactSent">
            <p className="secContactSent__title">送信されました。</p>
            <p className="secContactSent__txt">お問い合わせを受け付けました。</p>
          </section>}

        </main>
        <CorpFooter ref={ftr} />
      </div>
    </div>
  );
};
